import React, { ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import { LoadingSpinner, Notification, NotificationContainer } from '@mesa-labs/mesa-ui';
import { ProgramCodes } from '@mesa-labs/mesa-api/dist/types';

import { useDispatch } from '../../redux/hooks';
import FormContainer from '../common/FormContainer';
import HeaderContainer from './common/HeaderContainer';
import OnboardingHeader from '../common/OnboardingHeader';
import OnboardingSubHeader from '../common/OnboardingSubHeader';
import useStartDemoMode, { OPTION_TO_COGNITO_USERNAME, OPTION_DEMO_VENDOR_REQUIRING_PLAID_RECONNECTION } from '../../hooks/useStartDemoMode';
import { ExtraDemoCodes, resetDemoMode, type DemoNavigationTarget } from '../../redux/slices/demoMode';
import DemoModePasswordModal from './DemoModePasswordModal';

const Container = styled(FormContainer)``;

const Options = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionGroup = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: 20px;
  width: 100%;

  &:last-of-type {
    margin-bottom: 0px;
  }
`;

const Option = styled.div`
  align-items: center;
  display: flex;
  background: ${(props) => props.theme.colors.White};
  border: 1px solid #EAECF0;
  border-radius: 4px;
  box-shadow: none;
  cursor: pointer;
  justify-content: space-between;
  padding: 16px 20px;
  position: relative;
  transition: box-shadow 300ms ease-in-out;
  width: 100%;

  &:hover {
    box-shadow: 0px 1px 2px 0px #1018280D;
  }
`;

const OptionTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionTitle = styled.div`
  align-items: center;
  color: #0E121B;
  display: flex;
  font-size: 14px;
  font-weight: 500;
  justify-content: space-between;
  line-height: 20px;

  @media (max-width: ${(props) => props.theme.breakpoints.Mobile}) {
    font-size: 14px;
  }
`;

const OptionSeparatorText = styled.p`
  color: ${(props) => props.theme.colors.Purple};
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
`;

const LoadingSpinnerContainer = styled.div`
  position: absolute;
  top: calc(50% - 8px);
  right: -8px;
  transform: scale(0.7) translateY(-50%) translateX(-50%);
  transform-origin: center;
`;

type Option = keyof typeof OPTION_TO_COGNITO_USERNAME;

function DemoModeSelect(): ReactElement {
  const dispatch = useDispatch();

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const startDemoMode = useStartDemoMode();

  useEffect(() => {
    dispatch(resetDemoMode());
  }, []);

  const openModalWithOption = (option: Option) => {
    setSelectedOption(option);
    setShowPasswordModal(true);
  };

  const onSubmit = async ({ option, password, navigateTo }: { option: Option; password: string; navigateTo: DemoNavigationTarget }): Promise<void> => {
    dispatch({ type: 'SIGN_OUT' });
    setIsLoading(true);
    setSelectedOption(option);

    try {
      await startDemoMode({ option, password, navigateTo });
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="payment-schedule">
      <NotificationContainer portalRootId="notification-root" location="bottomCenter" position="fixed" bottom="32px">
        {error ? <Notification type="error" message={error} onClick={() => setError(undefined)} /> : <></>}
      </NotificationContainer>

      <DemoModePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={({ password, navigateTo }) => {
          if (!selectedOption || !password) {
            return;
          }

          onSubmit({ option: selectedOption, password, navigateTo });
        }}
        isLoading={isLoading}
      />

      <HeaderContainer>
        <OnboardingHeader>Select Demo Mode</OnboardingHeader>
        <OnboardingSubHeader>Please select one of the following options below.</OnboardingSubHeader>
      </HeaderContainer>

      <Options>
        <OptionSeparatorText>Standard modes</OptionSeparatorText>

        <OptionGroup>
          <Option
            key="classic"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.NET30_AND_POA)}
          >
            <OptionTitleContainer>
              <OptionTitle>Classic Mode</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.NET30_AND_POA && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="net-30"
            onClick={() => !isLoading && openModalWithOption(ProgramCodes.NET30)}
          >
            <OptionTitleContainer>
              <OptionTitle>Net-30</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ProgramCodes.NET30 && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="pay-on-approval"
            onClick={() => !isLoading && openModalWithOption(ProgramCodes.POA)}
          >
            <OptionTitleContainer>
              <OptionTitle>Pay on Approval</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ProgramCodes.POA && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="on-demand"
            onClick={() => !isLoading && openModalWithOption(ProgramCodes.OD)}
          >
            <OptionTitleContainer>
              <OptionTitle>On Demand</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ProgramCodes.OD && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>
        </OptionGroup>

        <OptionSeparatorText>Direct Debit modes</OptionSeparatorText>

        <OptionGroup>
          <Option
            key="direct-debit-corrigo-poa"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.DIRECT_DEBIT_CORRIGO_POA)}
            data-cy={OPTION_DEMO_VENDOR_REQUIRING_PLAID_RECONNECTION.CORRIGO}
          >
            <OptionTitleContainer>
              <OptionTitle>Corrigo</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.DIRECT_DEBIT_CORRIGO_POA && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="direct-debit-itrade-poa"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.DIRECT_DEBIT_ITRADE_POA)}
            data-cy={OPTION_DEMO_VENDOR_REQUIRING_PLAID_RECONNECTION.ITRADE}
          >
            <OptionTitleContainer>
              <OptionTitle>iTradeNetwork</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.DIRECT_DEBIT_ITRADE_POA && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="direct-debit-ghx-net30"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.DIRECT_DEBIT_GHX_NET30)}
            data-cy={OPTION_DEMO_VENDOR_REQUIRING_PLAID_RECONNECTION.DD_NET30}
          >
            <OptionTitleContainer>
              <OptionTitle>GHX</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.DIRECT_DEBIT_GHX_NET30 && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="direct-debit-od"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.DIRECT_DEBIT_OD)}
          >
            <OptionTitleContainer>
              <OptionTitle>On Demand</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.DIRECT_DEBIT_OD && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>
        </OptionGroup>

        <OptionSeparatorText>Branded modes</OptionSeparatorText>
        <OptionGroup>
          <Option
            key="direct-debit-tungsten-automation-net30"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.DIRECT_DEBIT_TUNGSTEN_AUTOMATION_ON_DEMAND)}
          >
            <OptionTitleContainer>
              <OptionTitle>Tungsten Automation</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.DIRECT_DEBIT_TUNGSTEN_AUTOMATION_ON_DEMAND && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="beeline"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.NET15_AND_POA_BEELINE)}
          >
            <OptionTitleContainer>
              <OptionTitle>Beeline</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.NET15_AND_POA_BEELINE && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="built"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.DIRECT_DEBIT_BUILT_POA)}
          >
            <OptionTitleContainer>
              <OptionTitle>Built</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.DIRECT_DEBIT_BUILT_POA && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="service_titan"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.DIRECT_DEBIT_SERVICE_TITAN_POA)}
          >
            <OptionTitleContainer>
              <OptionTitle>Service Titan</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.DIRECT_DEBIT_SERVICE_TITAN_POA && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="direct-commerce"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.DIRECT_DEBIT_DIRECT_COMMERCE_POA)}
          >
            <OptionTitleContainer>
              <OptionTitle>Direct Commerce</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.DIRECT_DEBIT_DIRECT_COMMERCE_POA && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="workday-vndly"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.DIRECT_DEBIT_WORKDAY_VNDLY_POA)}
          >
            <OptionTitleContainer>
              <OptionTitle>Workday VNDLY</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.DIRECT_DEBIT_WORKDAY_VNDLY_POA && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="tapfin"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.TAPFIN_NET30)}
          >
            <OptionTitleContainer>
              <OptionTitle>Tapfin</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.TAPFIN_NET30 && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="randstad"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.RANDSTAD_NET30)}
          >
            <OptionTitleContainer>
              <OptionTitle>Randstad</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.RANDSTAD_NET30 && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="kelly-ocg"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.KELLY_OCG_NET30)}
          >
            <OptionTitleContainer>
              <OptionTitle>Kelly OCG</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.KELLY_OCG_NET30 && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>

          <Option
            key="ags"
            onClick={() => !isLoading && openModalWithOption(ExtraDemoCodes.AGS_NET30)}
          >
            <OptionTitleContainer>
              <OptionTitle>AGS</OptionTitle>
            </OptionTitleContainer>

            {isLoading && selectedOption === ExtraDemoCodes.AGS_NET30 && (
              <LoadingSpinnerContainer>
                <LoadingSpinner />
              </LoadingSpinnerContainer>
            )}
          </Option>
        </OptionGroup>

        <OptionSeparatorText>Miscellaneous</OptionSeparatorText>
        <OptionGroup>
          <Option
            key="partner-portal"
            onClick={() => {
              window.open('https://sourcing.sandbox.joinmesa.com/sign-in-ops');
            }}
          >
            <OptionTitleContainer>
              <OptionTitle>Partner Portal</OptionTitle>
            </OptionTitleContainer>
          </Option>
        </OptionGroup>
      </Options>
    </Container>
  );
}

export default DemoModeSelect;
